import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const ContactSection = ({ data, theme, primaryColor, siteId }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const isDark = !['light_modern', 'colorful_kids', 'ivory_gold'].includes(theme);
  const isRed = theme === 'theatre_red';
  const darkInput = isRed
    ? "bg-[#140a0c]/90 border-[#3b2125] text-white placeholder:text-[#8a6f68] focus:border-[#d4af37]"
    : "bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500";
  const cardGradA = isRed ? 'rgba(26,10,12,0.9)' : 'rgba(30,41,59,0.8)';
  const cardGradB = isRed ? 'rgba(26,10,12,0.5)' : 'rgba(30,41,59,0.4)';
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contacts = data?.contacts || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/public/enquiries', { 
        ...form, 
        site_id: siteId,
        form_email: data?.form_email 
      });
      toast.success('Message sent successfully!');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className={cn("py-32 px-4 relative overflow-hidden", t.bg)}>
      {/* Background accents */}
      <div 
        className="absolute bottom-0 left-0 w-1/2 h-1/2 opacity-5"
        style={{ background: `radial-gradient(circle at bottom left, ${primaryColor} 0%, transparent 70%)` }}
      />
      <div 
        className="absolute top-0 right-0 w-1/3 h-1/3 opacity-5"
        style={{ background: `radial-gradient(circle at top right, ${primaryColor} 0%, transparent 70%)` }}
      />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Get In Touch
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold mb-4", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data?.title || 'Contact'}
          </h2>
          {data?.form_message && (
            <p className={cn("text-lg max-w-2xl mx-auto", t.textMuted)}>
              {data.form_message}
            </p>
          )}
        </AnimatedSection>
        
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Cards */}
          <AnimatedSection animation="slideRight" delay={200}>
            {contacts.length > 0 ? (
              <div className="space-y-6">
                {contacts.map((contact, idx) => (
                  <div 
                    key={contact.id || idx}
                    className={cn(
                      "p-6 rounded-2xl border backdrop-blur-sm transition-all duration-300 hover:scale-[1.02]",
                      t.card, 
                      t.border
                    )}
                    style={{ 
                      borderColor: primaryColor + '30',
                      background: `linear-gradient(135deg, ${isDark ? cardGradA : 'rgba(255,255,255,0.8)'} 0%, ${isDark ? cardGradB : 'rgba(255,255,255,0.4)'} 100%)`
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: primaryColor + '20' }}
                      >
                        <User className="w-5 h-5" style={{ color: primaryColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={cn("font-bold text-lg mb-1", t.text)}>
                          {contact.title || 'Contact'}
                        </h3>
                        {contact.name && (
                          <p className={cn("font-medium", t.text)}>{contact.name}</p>
                        )}
                        {contact.company && (
                          <p className={cn("text-sm", t.textMuted)}>{contact.company}</p>
                        )}
                        
                        <div className="mt-4 space-y-2">
                          {contact.email && (
                            <a 
                              href={`mailto:${contact.email}`}
                              className={cn("flex items-center gap-2 text-sm hover:underline group", t.textMuted)}
                            >
                              <Mail className="w-4 h-4" style={{ color: primaryColor }} />
                              <span className="group-hover:text-white transition-colors">{contact.email}</span>
                            </a>
                          )}
                          {contact.phone && (
                            <a 
                              href={`tel:${contact.phone.replace(/\s/g, '')}`}
                              className={cn("flex items-center gap-2 text-sm hover:underline group", t.textMuted)}
                            >
                              <Phone className="w-4 h-4" style={{ color: primaryColor }} />
                              <span className="group-hover:text-white transition-colors">{contact.phone}</span>
                            </a>
                          )}
                          {contact.address && (
                            <p className={cn("flex items-start gap-2 text-sm", t.textMuted)}>
                              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: primaryColor }} />
                              <span className="whitespace-pre-line">{contact.address}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Legacy single contact support */
              <div className="space-y-6">
                {(data?.email || data?.phone) && (
                  <div className={cn("p-6 rounded-2xl border", t.card, t.border)}>
                    {data?.email && (
                      <div className="flex items-center gap-4 mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: primaryColor + '20' }}
                        >
                          <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                        <div>
                          <p className={cn("text-sm", t.textMuted)}>Email</p>
                          <a href={`mailto:${data.email}`} className={cn("font-medium hover:underline", t.text)}>
                            {data.email}
                          </a>
                        </div>
                      </div>
                    )}
                    {data?.phone && (
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: primaryColor + '20' }}
                        >
                          <Phone className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                        <div>
                          <p className={cn("text-sm", t.textMuted)}>Phone</p>
                          <a href={`tel:${data.phone}`} className={cn("font-medium hover:underline", t.text)}>
                            {data.phone}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </AnimatedSection>

          {/* Contact Form - Chunky & Slick */}
          {data?.show_form !== false && (
            <AnimatedSection animation="slideLeft" delay={300}>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className={cn("text-sm font-medium", t.textMuted)}>Name *</label>
                  <input 
                    type="text"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className={cn(
                      "w-full px-5 py-4 rounded-xl border-2 text-lg transition-all duration-200",
                      "focus:outline-none focus:ring-0",
                      isDark 
                        ? darkInput
                        : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-orange-500"
                    )}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className={cn("text-sm font-medium", t.textMuted)}>Email *</label>
                    <input 
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      className={cn(
                        "w-full px-5 py-4 rounded-xl border-2 text-lg transition-all duration-200",
                        "focus:outline-none focus:ring-0",
                        isDark 
                          ? darkInput
                          : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-orange-500"
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className={cn("text-sm font-medium", t.textMuted)}>Phone</label>
                    <input 
                      type="tel"
                      placeholder="+44 7700 900000"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className={cn(
                        "w-full px-5 py-4 rounded-xl border-2 text-lg transition-all duration-200",
                        "focus:outline-none focus:ring-0",
                        isDark 
                          ? darkInput
                          : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-orange-500"
                      )}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className={cn("text-sm font-medium", t.textMuted)}>Message *</label>
                  <textarea 
                    placeholder="Tell us about your enquiry..."
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    rows={5}
                    className={cn(
                      "w-full px-5 py-4 rounded-xl border-2 text-lg transition-all duration-200 resize-none",
                      "focus:outline-none focus:ring-0",
                      isDark 
                        ? darkInput
                        : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-orange-500"
                    )}
                  />
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={cn(
                    "w-full py-5 rounded-xl text-lg font-semibold text-white transition-all duration-300",
                    "hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]",
                    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  )}
                  style={{ 
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 40px ${primaryColor}40`
                  }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send Message
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </button>
              </form>
            </AnimatedSection>
          )}
        </div>
      </div>
    </section>
  );
};


